import React, { useEffect, useState, useRef } from "react";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  Divider,
  IconButton,
  Tooltip,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Print, Email, WhatsApp } from "@mui/icons-material";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { axiosInstance } from "../../utils/commonImports";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";


const VoucherPrintPreview = ({ open, onClose, voucherId }) => {
  const [voucherData, setVoucherData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
  });

  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const printRef = useRef();

  useEffect(() => {
    if (open && voucherId) fetchVoucherData();
  }, [open, voucherId]);

  const fetchVoucherData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/api/vouchers/${voucherId}/print`);
      setVoucherData(res.data);
    } catch (err) {
      console.error("❌ Failed to load voucher print data:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- Helpers to read party/bank from voucher_lines safely ---
  const lines = voucherData?.voucher_lines || [];
  const drLine = lines.find((l) => Number(l.debit_amount) > 0); // Debit side
  const crLine = lines.find((l) => Number(l.credit_amount) > 0); // Credit side

  const vt = (voucherData?.voucher_type || "").toLowerCase();
  const isReceipt = vt === "receipt";
  const isPayment = vt === "payment";

  // Party (counterparty) and Instrument (cash/bank) per accounting rules
  const partyName =
    (isReceipt ? crLine?.name : drLine?.name) ||
    voucherData?.ledger_name ||
    "-";

  const throughName = (isReceipt ? drLine?.name : crLine?.name) || "-";

  const handlePrint = async () => {
    const input = printRef.current;
    if (!input) return;

    try {
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollY: 0,
        windowWidth: input.scrollWidth,
        windowHeight: input.scrollHeight + 500,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // ----- watermark + footer -----
      const addFooter = () => {
        if (voucherData?.voucher_type?.toLowerCase() === "receipt") {
          pdf.saveGraphicsState();
          pdf.setGState(new pdf.GState({ opacity: 0.08 }));
          pdf.setFontSize(100);
          pdf.setTextColor(0, 0, 0);
          pdf.text("PAID", pdfWidth / 2, pdfHeight / 2, {
            angle: -30,
            align: "center",
          });
          pdf.restoreGraphicsState();
        }

        pdf.setFontSize(9);
        pdf.setTextColor(80);
        const text = "Printed by WheelsERP";
        const textWidth =
          (pdf.getStringUnitWidth(text) * pdf.internal.getFontSize()) /
          pdf.internal.scaleFactor;
        pdf.text(text, pdfWidth - textWidth - 8, pdfHeight - 6);
      };

      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      addFooter();
      heightLeft -= pdfHeight;
      position = -pdfHeight;

      while (heightLeft > 0) {
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        addFooter();
        heightLeft -= pdfHeight;
        position -= pdfHeight;
      }

      // ----- single-file logic -----
      const fileName = `${voucherData?.voucher_type || "Voucher"}_${
        voucherData?.voucher_number || "Document"
      }.pdf`;

      // make blob only once
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // open it directly in a new tab (auto-open)
      const newTab = window.open(pdfUrl, "_blank");

      // trigger download with same blob (no second creation)
      const link = document.createElement("a");
      link.href = pdfUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // clean up after some seconds
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
    } catch (err) {
      console.error("❌ PDF generation failed:", err);
    }
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleOpenEmailModal = () => {
    setEmailData({
      to: "client@example.com", // default email
      subject: `Payment Receipt - ${
        voucherData?.client?.name || "Your Company"
      }`,
    });
    setIsModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setIsModalOpen(false);
  };

  const handleSendEmail = async () => {
    try {
      if (!voucherData?.voucher_id) {
        setSnackbar({
          open: true,
          message: "⚠️ Voucher ID missing — cannot send email.",
          severity: "warning",
        });
        return;
      }

      setIsSendingEmail(true);

      // 📄 Capture preview as PDF
      const canvas = await html2canvas(printRef.current, { scale: 2 });
      const pdf = new jsPDF("p", "mm", "a4");
      const imgData = canvas.toDataURL("image/png");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);

      const pdfBase64 = pdf.output("datauristring").split(",")[1];
      const fileName = `${voucherData?.voucher_type}_${voucherData?.voucher_number}.pdf`;

      // 📧 Email body content
      const htmlContent = {
        customerName: voucherData?.ledger_name || "Valued Customer",
        amount: voucherData?.total_amount?.toFixed(2) || "0.00",
        voucherNumber: voucherData?.voucher_number || "-",
        voucherDate: voucherData?.voucher_date || "-",
        narration: voucherData?.narration || "",
        clientName: voucherData?.client?.name || "Your Company",
      };

      console.log("📨 Sending email with data:", {
        to: emailData.to,
        subject: emailData.subject,
        fileName,
      });

      // 🚀 Send to backend
      const res = await axiosInstance.post(
        `/api/vouchers/vouchers/${voucherData.voucher_id}/send-email`,
        {
          to: emailData.to,
          subject: emailData.subject,
          htmlContent,
          pdfBase64,
          fileName,
        }
      );

      console.log("📩 Server response:", res.data);

      // ✅ Handle success properly
      if (
        res?.status === 200 &&
        (res.data?.success === true ||
          res.data?.message?.toLowerCase().includes("email sent"))
      ) {
        setSnackbar({
          open: true,
          message: "✅ Email sent successfully!",
          severity: "success",
        });
        setIsModalOpen(false);
      } else {
        console.warn("⚠️ Unexpected response from server:", res.data);
        setSnackbar({
          open: true,
          message:
            "⚠️ Email may have been sent, but server did not confirm. Check logs.",
          severity: "warning",
        });
      }
    } catch (err) {
      console.error("❌ Email send error:", err.response?.data || err.message);
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Unknown error while sending email.";
      setSnackbar({
        open: true,
        message: `❌ Failed to send email: ${msg}`,
        severity: "error",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  function convertNumberToWords(amount) {
    if (!amount) return "Zero Rupees Only";
    const a = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const b = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    function inWords(num) {
      if ((num = num.toString()).length > 9) return "Overflow";
      const n = ("000000000" + num)
        .substr(-9)
        .match(/^(\d{2})(\d{2})(\d{2})(\d{1})(\d{2})$/);
      if (!n) return;
      let str = "";
      str +=
        n[1] != 0
          ? (a[+n[1]] || b[n[1][0]] + " " + a[n[1][1]]) + " Crore "
          : "";
      str +=
        n[2] != 0 ? (a[+n[2]] || b[n[2][0]] + " " + a[n[2][1]]) + " Lakh " : "";
      str +=
        n[3] != 0
          ? (a[+n[3]] || b[n[3][0]] + " " + a[n[3][1]]) + " Thousand "
          : "";
      str +=
        n[4] != 0
          ? (a[+n[4]] || b[n[4][0]] + " " + a[n[4][1]]) + " Hundred "
          : "";
      str +=
        n[5] != 0
          ? (str !== "" ? "and " : "") +
            (a[+n[5]] || b[n[5][0]] + " " + a[n[5][1]]) +
            " "
          : "";
      return str.trim();
    }
    return inWords(Math.floor(amount)) + " Rupees Only";
  }

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: "95vh",
            overflow: "hidden",
            borderRadius: 2,
            p: 0,
          },
        }}
      >
        <DialogContent
          sx={{
            backgroundColor: "#f5f7fa",
            display: "flex",
            justifyContent: "center",
            overflowY: "auto",
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : voucherData ? (
            <Box
              ref={printRef}
              sx={{
                backgroundColor: "#fff",
                width: "100%",
                maxWidth: 720,
                mt: 3,
                mb: 4,
                borderRadius: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                position: "relative",
                p: 4,
                color: "#000",
                fontFamily: "Arial, sans-serif",
              }}
            >
              {/* Company Header - Two Column Layout (Left Aligned) */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  borderBottom: "1px solid #ccc",
                  pb: 1.5,
                }}
              >
                {/* Left Column - Logo */}
                <Box sx={{ flex: "0 0 25%", textAlign: "left" }}>
                  {voucherData.client?.logo && (
                    <img
                      src={`data:image/png;base64,${voucherData.client.logo}`}
                      alt="Logo"
                      style={{
                        width: 80, // smaller and clean
                        height: "auto",
                        objectFit: "contain",
                      }}
                    />
                  )}
                </Box>

                {/* Right Column - Company Details (Left Aligned) */}
                <Box
                  sx={{
                    flex: "0 0 70%",
                    textAlign: "left",
                    color: "#000",
                    lineHeight: 1.4,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: "bold", color: "#000" }}
                  >
                    {voucherData.client?.name || "Company Name"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    {voucherData.client?.address}, {voucherData.client?.city},{" "}
                    {voucherData.client?.state} - {voucherData.client?.zipcode}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#000" }}>
                    GSTIN: {voucherData.client?.gstin || "-"} | PAN:{" "}
                    {voucherData.client?.pan || "-"}
                  </Typography>
                </Box>
              </Box>

              {/* Title */}
              <Typography
                align="center"
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  textDecoration: "underline",
                  mb: 3,
                  color: "#000",
                }}
              >
                {voucherData.voucher_type?.toUpperCase() === "RECEIPT"
                  ? "PAYMENT RECEIPT"
                  : "PAYMENT VOUCHER"}
              </Typography>

              {/* Voucher Info */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  color: "#000",
                }}
              >
                <Box sx={{ width: "60%" }}>
                  {/* ===== Voucher Info (labels + values) ===== */}
                  <Typography sx={{ mb: 1 }}>
                    <strong>
                      {isReceipt ? "Payment Date:" : "Payment Date:"}
                    </strong>{" "}
                    {dayjs(voucherData.voucher_date).format("DD/MM/YYYY")}
                  </Typography>

                  <Typography sx={{ mb: 1 }}>
                    <strong>Voucher No:</strong> {voucherData.voucher_number}
                  </Typography>

                  {isReceipt ? (
                    <>
                      <Typography sx={{ mb: 1 }}>
                        <strong>Received From:</strong> {partyName}
                      </Typography>
                      <Typography sx={{ mb: 1 }}>
                        <strong>Received Through:</strong> {throughName}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ mb: 1 }}>
                        <strong>Paid To:</strong> {partyName}
                      </Typography>
                      <Typography sx={{ mb: 1 }}>
                        <strong>Paid Through:</strong> {throughName}
                      </Typography>
                    </>
                  )}

                  <Typography sx={{ mb: 1 }}>
                    <strong>Amount (in Words):</strong>{" "}
                    {convertNumberToWords(voucherData.total_amount || 0)}
                  </Typography>

                  {voucherData.narration && (
                    <Typography sx={{ mb: 1 }}>
                      <strong>Narration:</strong> {voucherData.narration}
                    </Typography>
                  )}
                </Box>

                {/* Amount box */}
                <Box
                  sx={{
                    width: "35%",
                    backgroundColor: "#2ecc71",
                    color: "#000",
                    borderRadius: 1,
                    p: 2,
                    textAlign: "center",
                    alignSelf: "center",
                    border: "1px solid #000",
                  }}
                >
                  <Typography>
                    Amount{" "}
                    {voucherData.voucher_type === "Receipt"
                      ? "Received"
                      : "Paid"}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    ₹ {Number(voucherData.total_amount || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {/* Adjusted Bills */}
              {voucherData.transactions?.length > 0 && (
                <Box sx={{ mt: 4 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", textDecoration: "underline" }}
                  >
                    Adjusted Bills
                  </Typography>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      marginTop: "8px",
                      fontSize: "13px",
                      color: "#000",
                    }}
                  >
                    <thead>
                      <tr style={{ backgroundColor: "#f3f3f3" }}>
                        {[
                          "Invoice No",
                          "Date",
                          "Bill Amount",
                          "Adjusted Amount",
                        ].map((header) => (
                          <th
                            key={header}
                            style={{
                              border: "1px solid rgba(0,0,0,0.4)",
                              padding: "6px",
                              verticalAlign: "middle",
                              textAlign: "center",
                              fontWeight: "bold",
                              backgroundColor: "#f3f3f3",
                            }}
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {voucherData.transactions.map((tx) => (
                        <tr key={tx.id}>
                          <td
                            style={{
                              border: "1px solid rgba(0,0,0,0.4)",
                              textAlign: "center",
                              padding: "6px",
                              verticalAlign: "middle",
                            }}
                          >
                            {tx.invoice_number || "-"}
                          </td>
                          <td
                            style={{
                              border: "1px solid rgba(0,0,0,0.4)",
                              textAlign: "center",
                              padding: "6px",
                              verticalAlign: "middle",
                            }}
                          >
                            {tx.invoice_date
                              ? dayjs(tx.invoice_date).format("DD/MM/YYYY")
                              : "-"}
                          </td>
                          <td
                            style={{
                              border: "1px solid rgba(0,0,0,0.4)",
                              textAlign: "center",
                              padding: "6px",
                              verticalAlign: "middle",
                            }}
                          >
                            ₹ {Number(tx.invoice_amount || 0).toFixed(2)}
                          </td>
                          <td
                            style={{
                              border: "1px solid rgba(0,0,0,0.4)",
                              textAlign: "center",
                              padding: "6px",
                              verticalAlign: "middle",
                            }}
                          >
                            ₹ {Number(tx.amount || 0).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              )}

              {/* Signatures */}
              <Box
                sx={{
                  mt: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  px: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    borderTop: "0.5px solid #000",
                    pt: 0.2,
                    width: "35%",
                    textAlign: "left",
                    color: "#000",
                  }}
                >
                  Receiver’s Signature
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    borderTop: "0.5px solid #000",
                    pt: 0.2,
                    width: "35%",
                    textAlign: "right",
                    color: "#000",
                  }}
                >
                  Authorized Signatory
                </Typography>
              </Box>

              {/* Footer */}
              <Typography
                align="center"
                variant="body2"
                sx={{ mt: 3, color: "#000" }}
              >
                Thank you for your business!
              </Typography>
            </Box>
          ) : (
            <Typography>No voucher data available.</Typography>
          )}
        </DialogContent>

        {/* Bottom Action Buttons (Preview only) */}
        <DialogActions
          sx={{
            justifyContent: "center",
            backgroundColor: "#f0f3f7",
            py: 1.5,
          }}
        >
          <Tooltip title="Print to PDF">
            <IconButton color="primary" onClick={handlePrint}>
              <Print />
            </IconButton>
          </Tooltip>
          <Tooltip title="Send Email">
            <IconButton color="secondary" onClick={handleOpenEmailModal}>
              <Email />
            </IconButton>
          </Tooltip>

          <Tooltip title="Send via WhatsApp">
            <IconButton
              sx={{ color: "#25D366" }}
              onClick={() =>
                window.open(
                  `https://wa.me/?text=Voucher%20Details:%20${window.location.href}`,
                  "_blank"
                )
              }
            >
              <WhatsApp />
            </IconButton>
          </Tooltip>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ================= Zoho-style Email Modal ================= */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseEmailModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Send Voucher by Email</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="To"
            fullWidth
            margin="dense"
            value={emailData.to}
            onChange={(e) => setEmailData({ ...emailData, to: e.target.value })}
          />
          <TextField
            label="Subject"
            fullWidth
            margin="dense"
            value={emailData.subject}
            onChange={(e) =>
              setEmailData({ ...emailData, subject: e.target.value })
            }
          />
          <div
            style={{
              marginTop: "1rem",
              padding: "1rem",
              border: "1px solid #ccc",
              borderRadius: "6px",
              backgroundColor: "#fafafa",
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            <p>
              Dear <strong>{voucherData?.ledger_name || "Customer"}</strong>,
            </p>
            <p>
              Thank you for your payment of ₹
              {voucherData?.total_amount?.toFixed(2) || "0.00"}.
            </p>
            <p>
              Voucher No: <strong>{voucherData?.voucher_number}</strong>
            </p>
            <p>
              Date: <strong>{voucherData?.voucher_date?.split("T")[0]}</strong>
            </p>
            <p>
              Narration: <em>{voucherData?.narration || "-"}</em>
            </p>
            <p>
              Best Regards,
              <br />
              {voucherData?.client?.name}
            </p>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailModal} disabled={isSendingEmail}>
            Cancel
          </Button>
          <Button
            onClick={handleSendEmail}
            variant="contained"
            color="primary"
            disabled={isSendingEmail}
            startIcon={isSendingEmail ? <CircularProgress size={20} /> : null}
          >
            {isSendingEmail ? "Sending..." : "Send Email"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default VoucherPrintPreview;
